import { isGa4FirstTouchEnabled } from '@/config/ga4FirstTouch.js';
import { ATTRIBUTION_STORAGE_KEY, SIGNUP_ATTRIBUTION_STORAGE_KEY } from '@/constants/tokens';
import {
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemToLocalStorage,
} from '@/helpers/localStorageServices.js';
import { countryForTimeZone } from '@/helpers/sellerRegionHint.js';

// Marketing query params that identify an acquisition source. Also the only params allowed
// into the stored landingPage — everything else (inviteCode, OAuth code/state, …) may be
// sensitive and must not be persisted or sent to the backend.
const MARKETING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
];

const LANDING_PAGE_MAX_LENGTH = 500;

// A bundle "has signal" when it carries at least one real acquisition marker (UTM or click id).
// referrer/landingPage alone don't count — every direct visit has those.
const hasSignal = (bundle) =>
  !!(
    bundle.utmSource ||
    bundle.utmMedium ||
    bundle.utmCampaign ||
    bundle.utmTerm ||
    bundle.utmContent ||
    bundle.gclid ||
    bundle.gbraid ||
    bundle.wbraid
  );

const sanitizedLandingPage = (params) => {
  const kept = new URLSearchParams();
  MARKETING_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) kept.set(key, value);
  });
  const query = kept.toString();
  const page = window.location.pathname + (query ? `?${query}` : '');
  return page.slice(0, LANDING_PAGE_MAX_LENGTH);
};

// Persist marketing attribution the moment the app loads (installAuth), before navigation
// strips the query string, so the acquisition source survives the register -> confirm flow
// and is still available to attach to /auth/confirm later. First-touch with an upgrade:
// the FIRST landing that carries a real signal (UTM / click id) wins — a signal-less bundle
// from an earlier direct visit is overwritten, so a later paid click isn't lost.
export const captureAttributionFromUrl = () => {
  const existing = getStoredAttribution();

  const params = new URLSearchParams(window.location.search);
  const firstTouch = isGa4FirstTouchEnabled();
  const bundle = {
    utmSource: params.get('utm_source') ?? (firstTouch ? params.get('wpp_src') : null),
    utmMedium: params.get('utm_medium') ?? (firstTouch ? params.get('wpp_med') : null),
    utmCampaign: params.get('utm_campaign'),
    utmTerm: params.get('utm_term'),
    utmContent:
      params.get('utm_content') ?? (firstTouch ? params.get('wpp_lp')?.slice(0, 200) : null) ?? null,
    gclid: params.get('gclid'),
    gbraid: params.get('gbraid'),
    wbraid: params.get('wbraid'),
    referrer: document.referrer || null,
    landingPage: sanitizedLandingPage(params),
    firstTouchAt: new Date().toISOString(),
  };

  if (existing && (hasSignal(existing) || !hasSignal(bundle))) return existing;

  setItemToLocalStorage(ATTRIBUTION_STORAGE_KEY, bundle);
  return bundle;
};

// Non-object guard: a corrupted/legacy raw string in localStorage must not ride the request.
export const getStoredAttribution = () => {
  const stored = getItemFromLocalStorage(ATTRIBUTION_STORAGE_KEY);
  return stored && typeof stored === 'object' ? stored : null;
};

// Browser context belongs to the signup request, not the stored first touch. Each signal is
// optional: unavailable or throwing browser APIs must never prevent confirmation or login.
export const getAttributionForRequest = () => {
  const attribution = { ...getStoredAttribution() };

  try {
    const language = navigator.language;
    if (language) attribution.browserLanguage = language;
  } catch { /* unavailable */ }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) attribution.timezone = timezone;
  } catch { /* unavailable */ }

  try {
    const countryHint = countryForTimeZone(attribution.timezone);
    if (countryHint) attribution.countryHint = countryHint;
  } catch { /* unavailable */ }

  return attribution;
};

// The manager's bundle is still cleared on every new session. Only a signup flow
// may retain these strings for the upcoming, one-shot bp_created event. With the GA4
// first-touch flag off, the signup key is neither written nor read.
export const clearStoredAttribution = ({ preserveForSignup = false } = {}) => {
  if (isGa4FirstTouchEnabled()) {
    try {
      removeItemFromLocalStorage(SIGNUP_ATTRIBUTION_STORAGE_KEY);
      if (preserveForSignup) {
        const attribution = getStoredAttribution();
        const firstTouch = {};
        ['utmSource', 'utmMedium', 'utmCampaign', 'landingPage'].forEach((key) => {
          const value = attribution?.[key];
          if (typeof value === 'string' && value.trim()) firstTouch[key] = value;
        });
        if (Object.keys(firstTouch).length) {
          setItemToLocalStorage(SIGNUP_ATTRIBUTION_STORAGE_KEY, firstTouch);
        }
      }
    } catch { /* optional analytics must not block authentication */ }
  }

  try {
    removeItemFromLocalStorage(ATTRIBUTION_STORAGE_KEY);
  } catch { /* storage may be unavailable */ }
};

export const clearSignupAttribution = () => {
  if (!isGa4FirstTouchEnabled()) return;
  try {
    removeItemFromLocalStorage(SIGNUP_ATTRIBUTION_STORAGE_KEY);
  } catch { /* optional analytics must not block sign-out */ }
};

export const consumeSignupAttribution = () => {
  if (!isGa4FirstTouchEnabled()) return null;
  try {
    const attribution = getItemFromLocalStorage(SIGNUP_ATTRIBUTION_STORAGE_KEY);
    removeItemFromLocalStorage(SIGNUP_ATTRIBUTION_STORAGE_KEY);
    return attribution && typeof attribution === 'object' ? attribution : null;
  } catch {
    clearSignupAttribution();
    return null;
  }
};
